import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Button,
} from '@mui/material';
import RenderSelectedFilters from './RenderSelectedFiltersProps';
import DropDownFilter from '../../pages/reports/RetailerAll/DropDownFilter';
import { Filters } from './CreateCohort';
import { fetchCohortFilterOptions } from '../../pages/assign-task/api';
import { useAlert } from '../../pages/AlertContext/AlertContext';

interface CreateCohortFilterProps {
  buttonText: string;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  token: string;
}

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface Task {
  id: string | number;
  name: string;
}

const CreateCohortFilter: React.FC<CreateCohortFilterProps> = ({
  buttonText,
  filters,
  setFilters,
  token,
}) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [asmNames, setAsmNames] = useState<Task[]>([]);
  const [territoryCodes, setTerritoryCodes] = useState<Task[]>([]);
  const [outletTypes, setOutletTypes] = useState<Task[]>([]);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const { showAlert } = useAlert();

  const tabsConfig = [

    { label: 'City', key: 'locations' },
    { label: 'Region', key: 'region' },
        { label: 'Outlet Category', key: 'outletTypes' },
    { label: 'Status', key: 'status' },
    { label: 'Contract Since', key: 'Contract_since' },
    { label: 'Current Rental Value', key: 'Current_rental_value' },
  ];

  // Normalize case for consistency
  const normalizeCase = (str: string) =>
    str && typeof str === 'string'
      ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
      : '';

  // Normalize Contract_since values to match backend expectations
  const normalizeContractSince = (range: string) => {
    if (!range || typeof range !== 'string') return '';
    const normalized = range.toLowerCase().replace(/\s/g, '');
    const validRanges: { [key: string]: string } = {
      'last1year': 'Last1Year',
      'last3year': 'Last3Year',
      'last6year': 'Last6Year',
      'last1yr': 'Last1Year',
      'last3yr': 'Last3Year',
      'last6yr': 'Last6Year',
      '1year': 'Last1Year',
      '3year': 'Last3Year',
      '6year': 'Last6Year',
    };
    const result = validRanges[normalized] || '';
    if (!result) {
      console.warn(`Invalid Contract_since value: ${range}`);
    }
    return result;
  };

  // Normalize outlet types to handle inconsistencies
  const normalizeOutletType = (type: string) =>
    type
      .replace(/non\s*paid/i, 'Nonpaid')
      .replace(/paid,\s*paid/i, 'Paid')
      .replace(/non\s*paid,\s*non\s*paid/i, 'Nonpaid')
      .replace(/non\s*paid,\s*paid/i, 'Nonpaid,Paid')
      .replace(/paid,\s*non\s*paid/i, 'Paid,Nonpaid');

  // Format Current_rental_value labels with ₹ symbol
  const formatRentalValueLabel = (range: string) => {
    if (range === 'Above 6000') {
      return `Above ₹6000`;
    }
    if (/^(\d+)-(\d+)$/.test(range)) {
      const [start, end] = range.split('-');
      return `₹${start}-₹${end}`;
    }
    return range;
  };

  // Debounce utility
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch filter options
  const fetchFilterOptions = useCallback(
    async (tabKey: string, updatedFilters: Filters) => {
      try {
        const selectedFilters = {
          locations: Object.entries(updatedFilters.locations)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          outlet_type: Object.entries(updatedFilters.outletTypes)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeOutletType(key)),
          region: Object.entries(updatedFilters.region)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          status: Object.entries(updatedFilters.status)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          Contract_since: Object.entries(updatedFilters.Contract_since)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeContractSince(key))
            .filter(Boolean),
          Current_rental_value: Object.entries(updatedFilters.Current_rental_value)
            .filter(([key, value]) => value)
            .map(([key]) => key),
          asm_names: Object.entries(updatedFilters.asmNames)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          territory_codes: Object.entries(updatedFilters.territoryCodes)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          outlet_names: Object.entries(updatedFilters.taskNames)
            .filter(([key, value]) => value)
            .map(([key]) => normalizeCase(key)),
          cohortName: '',
          task_id: '',
        };

        console.log('Fetching filters for:', tabKey, 'Payload:', JSON.stringify(selectedFilters, null, 2));

        const response = await fetchCohortFilterOptions(selectedFilters, token);
        console.log('API response:', JSON.stringify(response.data, null, 2));

        const data = response.data || {};

        const {
          locationsall = [],
          outlet_types = [],
          statusdata = [],
          regions = [],
          Current_rental_value_data = [],
          Contract_since_Data = [],
          outlet_names = [],
          asmnames = [],
          territoryCode = [],
        } = data;

        const validRentalValues = Current_rental_value_data.filter((range: string) =>
          /^(\d+)-(\d+)$/.test(range) || range === 'Above 6000'
        );

        setFilters((prevFilters) => {
          const newFilters = { ...prevFilters };

          newFilters.outletTypes = Array.isArray(outlet_types)
            ? outlet_types.reduce((acc: { [key: string]: boolean }, type: string) => {
              const normalizedType = normalizeOutletType(type);
              acc[normalizedType] = prevFilters.outletTypes[normalizedType] || false;
              return acc;
            }, {})
            : prevFilters.outletTypes;

          newFilters.locations = Array.isArray(locationsall)
            ? locationsall.reduce((acc: { [key: string]: boolean }, city: string) => {
              acc[normalizeCase(city)] = prevFilters.locations[normalizeCase(city)] || false;
              return acc;
            }, {})
            : prevFilters.locations;

          newFilters.region = Array.isArray(regions)
            ? regions.reduce((acc: { [key: string]: boolean }, region: { id: string; label: string }) => {
              acc[normalizeCase(region.label)] = prevFilters.region[normalizeCase(region.label)] || false;
              return acc;
            }, {})
            : prevFilters.region;

          newFilters.status = Array.isArray(statusdata)
            ? statusdata.reduce((acc: { [key: string]: boolean }, status: string) => {
              acc[normalizeCase(status)] = prevFilters.status[normalizeCase(status)] || false;
              return acc;
            }, {})
            : prevFilters.status;

          newFilters.Contract_since = Array.isArray(Contract_since_Data)
            ? Contract_since_Data.map((range: string) => normalizeContractSince(range))
              .filter(Boolean)
              .reduce((acc: { [key: string]: boolean }, range: string) => {
                acc[range] = prevFilters.Contract_since[range] || false;
                return acc;
              }, {})
            : prevFilters.Contract_since;

          newFilters.Current_rental_value = Array.isArray(validRentalValues)
            ? validRentalValues.reduce((acc: { [key: string]: boolean }, range: string) => {
              acc[range] = prevFilters.Current_rental_value[range] || false;
              return acc;
            }, {})
            : prevFilters.Current_rental_value;

          newFilters.taskNames = Array.isArray(outlet_names)
            ? outlet_names.reduce((acc: { [key: string]: boolean }, item: { id: string | number; name: string }) => {
              acc[item.id.toString()] = prevFilters.taskNames[item.id.toString()] || false;
              return acc;
            }, {})
            : prevFilters.taskNames;

          newFilters.asmNames = Array.isArray(asmnames)
            ? asmnames.reduce((acc: { [key: string]: boolean }, asm: { id: string; name: string }) => {
              acc[normalizeCase(asm.name)] = prevFilters.asmNames[normalizeCase(asm.name)] || false;
              return acc;
            }, {})
            : prevFilters.asmNames;

          newFilters.territoryCodes = Array.isArray(territoryCode)
            ? territoryCode.reduce((acc: { [key: string]: boolean }, territory: { id: string; name: string }) => {
              acc[normalizeCase(territory.name)] = prevFilters.territoryCodes[normalizeCase(territory.name)] || false;
              return acc;
            }, {})
            : prevFilters.territoryCodes;

          return newFilters;
        });

        const newTasks = Array.isArray(outlet_names)
          ? outlet_names
            .filter((item): item is { id: string | number; name: string } => !!item && !!item.name && typeof item.name === 'string')
            .map((item) => ({ id: item.id, name: normalizeCase(item.name) }))
          : tasks;
        const newAsmNames = Array.isArray(asmnames)
          ? asmnames
            .filter((asm): asm is { id: string; name: string } => !!asm && !!asm.name && typeof asm.name === 'string')
            .map((asm) => ({ id: normalizeCase(asm.name), name: normalizeCase(asm.name) }))
          : asmNames;
        const newTerritoryCodes = Array.isArray(territoryCode)
          ? territoryCode
            .filter((territory): territory is { id: string; name: string } => !!territory && !!territory.name && typeof territory.name === 'string')
            .map((territory) => ({ id: normalizeCase(territory.name), name: normalizeCase(territory.name) }))
          : territoryCodes;
        const newOutletTypes = Array.isArray(outlet_types)
          ? outlet_types
            .filter((type): type is string => !!type && typeof type === 'string')
            .map((type) => ({
              id: normalizeOutletType(type),
              name: normalizeOutletType(type),
            }))
          : outletTypes;

        setTasks(newTasks);
        setAsmNames(newAsmNames);
        setTerritoryCodes(newTerritoryCodes);
        setOutletTypes(newOutletTypes);

        console.log('Updated state:', { tasks: newTasks, asmNames: newAsmNames, territoryCodes: newTerritoryCodes, outletTypes: newOutletTypes });
      } catch (error) {
        console.error(`Error fetching filter options for ${tabKey}:`, error);
        showAlert(`Failed to load ${tabsConfig.find((tab) => tab.key === tabKey)?.label || tabKey} options.`, 'error');
      }
    },
    [token, setFilters, showAlert]
  );

  const debouncedFetchFilterOptions = useCallback(debounce(fetchFilterOptions, 300), [fetchFilterOptions]);

  // Initialize filters
  useEffect(() => {
    const initializeFilters = async () => {
      try {
        const response = await fetchCohortFilterOptions(
          {
            locations: [],
            outlet_type: [],
            region: [],
            status: [],
            Contract_since: [],
            Current_rental_value: [],
            asm_names: [],
            territory_codes: [],
            outlet_names: [],
            cohortName: '',
            task_id: '',
          },
          token
        );

        console.log('Initial API response:', JSON.stringify(response.data, null, 2));

        const data = response.data || {};

        const {
          asmnames = [],
          territoryCode = [],
          outlet_types = [],
          locationsall = [],
          regions = [],
          statusdata = [],
          Contract_since_Data = [],
          Current_rental_value_data = [],
          outlet_names = [],
        } = data;

        setFilters((prevFilters) => ({
          ...prevFilters,
          outletTypes: Array.isArray(outlet_types)
            ? outlet_types.reduce((acc: { [key: string]: boolean }, type: string) => {
              const normalizedType = normalizeOutletType(type);
              acc[normalizedType] = prevFilters.outletTypes[normalizedType] || false;
              return acc;
            }, {})
            : prevFilters.outletTypes,
          locations: Array.isArray(locationsall)
            ? locationsall.reduce((acc: { [key: string]: boolean }, city: string) => {
              acc[normalizeCase(city)] = prevFilters.locations[normalizeCase(city)] || false;
              return acc;
            }, {})
            : prevFilters.locations,
          region: Array.isArray(regions)
            ? regions.reduce((acc: { [key: string]: boolean }, region: { id: string; label: string }) => {
              acc[normalizeCase(region.label)] = prevFilters.region[normalizeCase(region.label)] || false;
              return acc;
            }, {})
            : prevFilters.region,
          status: Array.isArray(statusdata)
            ? statusdata.reduce((acc: { [key: string]: boolean }, status: string) => {
              acc[normalizeCase(status)] = prevFilters.status[normalizeCase(status)] || false;
              return acc;
            }, {})
            : prevFilters.status,
          Contract_since: Array.isArray(Contract_since_Data)
            ? Contract_since_Data.map((range: string) => normalizeContractSince(range))
              .filter(Boolean)
              .reduce((acc: { [key: string]: boolean }, range: string) => {
                acc[range] = prevFilters.Contract_since[range] || false;
                return acc;
              }, {})
            : { Last1Year: false, Last3Year: false, Last6Year: false },
          Current_rental_value: Array.isArray(Current_rental_value_data)
            ? Current_rental_value_data
              .filter((range: string) => /^(\d+)-(\d+)$/.test(range) || range === 'Above 6000')
              .reduce((acc: { [key: string]: boolean }, range: string) => {
                acc[range] = prevFilters.Current_rental_value[range] || false;
                return acc;
              }, {})
            : prevFilters.Current_rental_value,
          asmNames: Array.isArray(asmnames)
            ? asmnames.reduce((acc: { [key: string]: boolean }, asm: { id: string; name: string }) => {
              acc[normalizeCase(asm.name)] = prevFilters.asmNames[normalizeCase(asm.name)] || false;
              return acc;
            }, {})
            : prevFilters.asmNames,
          territoryCodes: Array.isArray(territoryCode)
            ? territoryCode.reduce((acc: { [key: string]: boolean }, territory: { id: string; name: string }) => {
              acc[normalizeCase(territory.name)] = prevFilters.territoryCodes[normalizeCase(territory.name)] || false;
              return acc;
            }, {})
            : prevFilters.territoryCodes,
          taskNames: Array.isArray(outlet_names)
            ? outlet_names.reduce((acc: { [key: string]: boolean }, item: { id: string | number; name: string }) => {
              acc[item.id.toString()] = prevFilters.taskNames[item.id.toString()] || false;
              return acc;
            }, {})
            : prevFilters.taskNames,
        }));

        const newTasks = Array.isArray(outlet_names)
          ? outlet_names
            .filter((item): item is { id: string | number; name: string } => !!item && !!item.name && typeof item.name === 'string')
            .map((item) => ({ id: item.id, name: normalizeCase(item.name) }))
          : [];
        const newAsmNames = Array.isArray(asmnames)
          ? asmnames
            .filter((asm): asm is { id: string; name: string } => !!asm && !!asm.name && typeof asm.name === 'string')
            .map((asm) => ({ id: normalizeCase(asm.name), name: normalizeCase(asm.name) }))
          : [];
        const newTerritoryCodes = Array.isArray(territoryCode)
          ? territoryCode
            .filter((territory): territory is { id: string; name: string } => !!territory && !!territory.name && typeof territory.name === 'string')
            .map((territory) => ({ id: normalizeCase(territory.name), name: normalizeCase(territory.name) }))
          : [];
        const newOutletTypes = Array.isArray(outlet_types)
          ? outlet_types
            .filter((type): type is string => !!type && typeof type === 'string')
            .map((type) => ({
              id: normalizeOutletType(type),
              name: normalizeOutletType(type),
            }))
          : [];

        setTasks(newTasks);
        setAsmNames(newAsmNames);
        setTerritoryCodes(newTerritoryCodes);
        setOutletTypes(newOutletTypes);

        console.log('Initial state:', { tasks: newTasks, asmNames: newAsmNames, territoryCodes: newTerritoryCodes, outletTypes: newOutletTypes });
        console.log('Initialized Contract_since:', filters.Contract_since);

        setFiltersInitialized(true);
      } catch (error) {
        console.error('Error initializing filters:', error);
        showAlert('Failed to initialize filter options.', 'error');
      }
    };

    initializeFilters();
  }, [token, setFilters, showAlert]);

  // Fallback for outletTypes if empty
  useEffect(() => {
    if (!outletTypes.length && filtersInitialized) {
      debouncedFetchFilterOptions('outletTypes', filters);
    }
  }, [outletTypes.length, filtersInitialized, debouncedFetchFilterOptions, filters]);

  const taskOptions: Option[] = tasks.map((task) => ({
    label: task.name,
    value: task.id.toString(),
  }));

  const asmOptions: Option[] = asmNames.map((asm) => ({
    label: asm.name,
    value: asm.name,
  }));

  const territoryOptions: Option[] = territoryCodes.map((territory) => ({
    label: territory.name,
    value: territory.name,
  }));

  const outletTypeOptions: Option[] = outletTypes.map((type) => ({
    label: type.name,
    value: type.name,
  }));

  useEffect(() => {
    console.log('Options for DropDownFilter:', { taskOptions, asmOptions, territoryOptions, outletTypeOptions });
  }, [taskOptions, asmOptions, territoryOptions, outletTypeOptions]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const tabKey = tabsConfig[newValue].key;
    debouncedFetchFilterOptions(tabKey, filters);
  };

  const handleFilterChange = (tabKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    const normalizedName = tabKey === 'Contract_since' ? normalizeContractSince(name) : normalizeCase(name);
    if (tabKey === 'Contract_since' && !normalizedName) {
      console.warn(`Skipping invalid Contract_since value: ${name}`);
      return;
    }
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [tabKey]: { ...prevFilters[tabKey], [normalizedName]: checked },
      };
      console.log('Updated filters (tab):', JSON.stringify(updatedFilters, null, 2));
      debouncedFetchFilterOptions(tabKey, updatedFilters);
      return updatedFilters;
    });
  };

  const handleSelectAll = (tabKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        [tabKey]: Object.keys(prevFilters[tabKey]).reduce((acc, key) => {
          const normalizedKey = tabKey === 'Contract_since' ? normalizeContractSince(key) : normalizeCase(key);
          if (tabKey === 'Contract_since' && !normalizedKey) return acc;
          acc[normalizedKey] = checked;
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      console.log('Updated filters (select all):', JSON.stringify(updatedFilters, null, 2));
      debouncedFetchFilterOptions(tabKey, updatedFilters);
      return updatedFilters;
    });
  };

  const handleTaskSelection = (selected: string[]) => {
    console.log('handleTaskSelection - Selected IDs:', selected);
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        taskNames: Object.keys(prevFilters.taskNames).reduce((acc, taskId) => {
          acc[taskId] = selected.includes(taskId);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      console.log('handleTaskSelection:', { selected, updatedFilters });
      debouncedFetchFilterOptions('taskNames', updatedFilters);
      return updatedFilters;
    });
  };

  const handleAsmSelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        asmNames: Object.keys(prevFilters.asmNames).reduce((acc, asm) => {
          acc[asm] = selected.includes(asm);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      console.log('handleAsmSelection:', { selected, updatedFilters });
      debouncedFetchFilterOptions('asmNames', updatedFilters);
      return updatedFilters;
    });
  };

  const handleTerritorySelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        territoryCodes: Object.keys(prevFilters.territoryCodes).reduce((acc, territory) => {
          acc[territory] = selected.includes(territory);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      console.log('handleTerritorySelection:', { selected, updatedFilters });
      debouncedFetchFilterOptions('territoryCodes', updatedFilters);
      return updatedFilters;
    });
  };

  const handleOutletTypeSelection = (selected: string[]) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        outletTypes: Object.keys(prevFilters.outletTypes).reduce((acc, type) => {
          acc[type] = selected.includes(type);
          return acc;
        }, {} as { [key: string]: boolean }),
      };
      console.log('handleOutletTypeSelection:', { selected, updatedFilters });
      debouncedFetchFilterOptions('outletTypes', updatedFilters);
      return updatedFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        outletTypes: Object.fromEntries(
          Object.keys(prevFilters.outletTypes).map((key) => [normalizeOutletType(key), false])
        ),
        locations: Object.fromEntries(
          Object.keys(prevFilters.locations).map((key) => [normalizeCase(key), false])
        ),
        region: Object.fromEntries(
          Object.keys(prevFilters.region).map((key) => [normalizeCase(key), false])
        ),
        taskHistory: { recent: false, older: false },
        taskType: { urgent: false, normal: false },
        status: Object.fromEntries(
          Object.keys(prevFilters.status).map((key) => [normalizeCase(key), false])
        ),
        Contract_since: Object.fromEntries(
          ['Last1Year', 'Last3Year', 'Last6Year'].map((key) => [key, false])
        ),
        Current_rental_value: Object.fromEntries(
          Object.keys(prevFilters.Current_rental_value).map((key) => [key, false])
        ),
        taskNames: Object.fromEntries(
          Object.keys(prevFilters.taskNames).map((key) => [key, false])
        ),
        asmNames: Object.fromEntries(
          Object.keys(prevFilters.asmNames).map((key) => [normalizeCase(key), false])
        ),
        territoryCodes: Object.fromEntries(
          Object.keys(prevFilters.territoryCodes).map((key) => [normalizeCase(key), false])
        ),
      };
      console.log('Cleared filters:', JSON.stringify(updatedFilters, null, 2));
      debouncedFetchFilterOptions('all', updatedFilters);
      return updatedFilters;
    });
  };

  const handleDelete = (tabKey: string, value: string) => {
    setFilters((prevFilters) => {
      const normalizedValue =
        tabKey === 'Contract_since'
          ? normalizeContractSince(value)
          : tabKey === 'outletTypes'
            ? normalizeOutletType(value)
            : tabKey === 'Current_rental_value'
              ? value
              : normalizeCase(value);

      if ((tabKey === 'Contract_since' && !normalizedValue) || (tabKey === 'Current_rental_value' && !normalizedValue)) {
        console.warn(`Skipping invalid ${tabKey} value: ${value}`);
        return prevFilters;
      }

      const updatedFilters = {
        ...prevFilters,
        [tabKey]: {
          ...prevFilters[tabKey],
          [normalizedValue]: false,
        },
      };

      console.log(
        `Deleted filter: ${value} from ${tabKey}`,
        'Updated filters:',
        JSON.stringify(updatedFilters, null, 2)
      );

      debouncedFetchFilterOptions(tabKey, updatedFilters);
      return updatedFilters;
    });
  };

  const capitalize = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1);

  // Determine if "Select All" should be shown based on number of options
  const shouldShowSelectAll = (tabKey: string) => {
    return Object.keys(filters[tabKey]).length >= 2;
  };

  // Determine if "Select All" should be shown for static cohort dropdowns
  const shouldShowSelectAllForDropdown = (options: Option[]) => {
    return options.length >= 2;
  };
// Format Contract_since for UI labels
const formatContractSinceLabel = (value: string) => {
  const mapping: Record<string, string> = {
    Last1Year: 'Last 1 Year',
    Last2Year: 'Last 2 Year',
    Last3Year: 'Last 3 Year',
    Last6Year: 'Last 6 Year',
  };
  return mapping[value] || value;
};

const hasActiveFilters = (filters: Record<string, Record<string, boolean>>): boolean => {
  return Object.values(filters).some(subFilter =>
    Object.values(subFilter).some(Boolean)
  );
};
console.log(filters, "filtersfilters333333333333")
  return (
    <Box>
      {buttonText !== 'Static Cohort' && (
        <Box
          sx={{
            width: '415px',
            right: '3%',
            backgroundColor: '#fff',
            padding: '20px 0',
            borderRadius: '10px',
            display: 'flex',
          }}
        >
          <Box sx={{ width: '200px', borderRight: '1px solid #ddd' }}>
            <Tabs
              orientation="vertical"
              value={selectedTab}
              onChange={handleTabChange}
              aria-label="Filter tabs"
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: '#282829',
                fontWeight: '500',
                textAlign: 'left',
              }}
            >
              {tabsConfig.map((tab, index) => (
                <Tab
                  key={tab.key}
                  label={tab.label}
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    color: '#282829',
                    fontWeight: '500',
                    textTransform: 'capitalize',
                    alignItems: 'start',
                    '&:hover': { backgroundColor: '#EEF4FF' },
                    '&.Mui-selected': { backgroundColor: '#EEF4FF', color: '#000' },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ paddingLeft: '20px', flexGrow: 1 }}>
            {/* <Box sx={{ marginBottom: '16px' }}>
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                sx={{
                  marginBottom: '10px',
                  textTransform: 'none',
                  color: '#C8172F',
                  borderColor: '#C8172F',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#C8172F',
                  },
                }}
              >
                Clear All Filters
              </Button>
            </Box> */}
            {tabsConfig.map((tab, index) =>
              selectedTab === index ? (
                <Box key={tab.key} sx={{ marginBottom: '16px' }}>
                  {shouldShowSelectAll(tab.key) && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Object.values(filters[tab.key]).every((value) => value && value !== undefined)}
                          onChange={(event) => handleSelectAll(tab.key, event)}
                          name="selectAll"
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "14px" }}>
                          Select All
                        </Typography>
                      }
                      sx={{ marginBottom: '1px', display: 'flex' }}
                    />
                  )}
                  {/* {Object.keys(filters[tab.key]).map((filterKey) => (
                    <FormControlLabel
                      key={filterKey}
                      sx={{ display: 'flex' }}
                      control={
                        <Checkbox
                          checked={filters[tab.key][filterKey] || false}
                          onChange={(event) => handleFilterChange(tab.key, event)}
                          name={filterKey}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "14px" }}>
                          {tab.key === "Current_rental_value"
                            ? formatRentalValueLabel(filterKey)
                            : capitalize(filterKey)}
                        </Typography>
                      }
                    />
                  ))} */}
                  {Object.keys(filters[tab.key]).map((filterKey) => (
  <FormControlLabel
    key={filterKey}
    sx={{ display: 'block' }}
    control={
      <Checkbox
        checked={filters[tab.key][filterKey] || false}
        onChange={(event) => handleFilterChange(tab.key, event)}
        name={filterKey}
      />
    }
    // label={
    //   tab.key === 'Current_rental_value'
    //     ? formatRentalValueLabel(filterKey)
    //     : tab.key === 'Contract_since'
    //     ? formatContractSinceLabel(filterKey)   // 👈 Use formatter here
    //     : capitalize(filterKey)
    // }
     label={
      tab.key === 'Current_rental_value'
        ? formatRentalValueLabel(filterKey)
        : tab.key === 'Contract_since'
        ? formatContractSinceLabel(filterKey)
        : tab.key === 'locations'
        ? filterKey.toUpperCase() // 👈 force uppercase
        : capitalize(filterKey)
    }
  />
))}

                </Box>
              ) : null
            )}
          </Box>
        </Box>
      )}

      {buttonText === 'Static Cohort' && (
        <Box sx={{}}>

<Box
            sx={{
              minWidth: 150,
              width: '398px',
              color: '#DEDEDE',
              background: '#fff !important',
              position: 'relative',
              top: '-23px',
              borderRadius: '8px',
              fontWeight: '500',
              justifyContent: 'center',
              backgroundColor: '#FFF',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, color: '#282829', fontSize: '16px', mb: 1 }}
            >
              Select ASM Names
            </Typography>
            <DropDownFilter
              options={asmOptions}
              value={Object.keys(filters.asmNames).filter((key) => filters.asmNames[key])}
              onChange={handleAsmSelection}
              placeholder="Select ASM Names"
              tabKey="asmNames"
              maxMenuHeight={300}
              showSelectAll={shouldShowSelectAllForDropdown(asmOptions)}
            />
          </Box>

          <Box
            sx={{
              minWidth: 150,
              width: '398px',
              color: '#DEDEDE',
              background: '#fff !important',
              position: 'relative',
              top: '-23px',
              borderRadius: '8px',
              fontWeight: '500',
              justifyContent: 'center',
              backgroundColor: '#FFF',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, color: '#282829', fontSize: '16px', mb: 1 }}
            >
              Select Territory Code
            </Typography>
            <DropDownFilter
              options={territoryOptions}
              value={Object.keys(filters.territoryCodes).filter((key) => filters.territoryCodes[key])}
              onChange={handleTerritorySelection}
              placeholder="Select Territory Code"
              tabKey="territoryCodes"
              maxMenuHeight={300}
              showSelectAll={shouldShowSelectAllForDropdown(territoryOptions)}
            />
          </Box>
          
          <Box
            sx={{
              minWidth: 150,
              width: '394px',
              color: '#DEDEDE',
              background: '#fff !important',
              position: 'relative',
              top: '-23px',
              borderRadius: '8px',
              fontWeight: '500',
              justifyContent: 'center',
              backgroundColor: '#FFF',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, color: '#282829', fontSize: '16px', mb: 1 }}
            >
              Select Outlet Category
            </Typography>
            <DropDownFilter
              options={outletTypeOptions}
              value={Object.keys(filters.outletTypes).filter((key) => filters.outletTypes[key])}
              onChange={handleOutletTypeSelection}
              placeholder="Select Outlet Category"
              tabKey="outletTypes"
              maxMenuHeight={300}
              showSelectAll={shouldShowSelectAllForDropdown(outletTypeOptions)}
            />
          </Box>
          <Box
            sx={{
              minWidth: 150,
              width: '398px',
              color: '#DEDEDE',
              background: '#fff !important',
              position: 'relative',
              top: '-23px',
              borderRadius: '8px',
              fontWeight: '500',
              justifyContent: 'center',
              backgroundColor: '#FFF',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, color: '#282829', fontSize: '16px', mb: 1 }}
            >
              Select Outlet Names
            </Typography>
            <DropDownFilter
              options={taskOptions}
              value={Object.keys(filters.taskNames).filter((key) => filters.taskNames[key])}
              onChange={handleTaskSelection}
              placeholder="Select Outlet Names"
              tabKey="taskNames"
              maxMenuHeight={300}
              showSelectAll={shouldShowSelectAllForDropdown(taskOptions)}
            />
          </Box>
          
          
        </Box>
      )}

      <Box sx={{ marginTop: '0px' }}>
        <RenderSelectedFilters
          filters={filters}
          maxVisible={3}
          showAllInitially={false}
          showTitle={true}
          title="Applied Filters"
          showCloseBtn={true}
          onDelete={handleDelete}
          tasks={tasks}
          formatRentalValueLabel={formatRentalValueLabel}
            formatContractSinceLabel={formatContractSinceLabel}
        />
      </Box>
{hasActiveFilters(filters) && (
      <Button
        variant="outlined"
        onClick={clearAllFilters}
        sx={{
          marginBottom: '20px',
          textTransform: 'none',
          color: '#C8172F',
          borderColor: '#C8172F',
          position: "absolute",
          bottom: "0px",
          left: "80px",
          height: "42px",
          borderRadius: "10px",
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#C8172F',
          },
        }}
      >
        Clear All Filters
      </Button>
      )}
    </Box>
  );
};

export default CreateCohortFilter;